import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Typography from '@ui/Typography';
import TypographyLink from '@ui/TypographyLink';
import Button from '@ui/Button';
import ModalV2 from '../../Modal/ModalV2';

export interface UserConsentModalProps {
  open: boolean;
  onClick: () => void;
  loading: boolean;
}

const ETHICAL_PROMPT_MODAL_ID = 'wsl-ethical-prompt-modal';

const useStyles = makeStyles()(theme => ({
  header: {
    backgroundColor: palette.gray_800,
    borderRadius: theme.spacing(0.5, 0.5, 0, 0),
    padding: theme.spacing(2, 3),
  },
  title: {
    margin: theme.spacing(0.25, 0),
  },

  modalBody: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    '& ul': {
      paddingLeft: theme.spacing(1),
      lineHeight: '1.7',
      listStylePosition: 'inside',
    },
    '& li::marker': {
      content: '"• "',
    },
    '& li span': {
      paddingLeft: theme.spacing(0.75),
    },
  },
  bodyHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'flex-start',
    gap: theme.spacing(2),
  },
  heartIcon: {
    height: '30px',
  },
  bodyParagraph: {
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'flex-start',
    gap: theme.spacing(1),
  },
  typographyBlock: {
    lineHeight: '1',
  },

  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignContent: 'center',
    padding: theme.spacing(3, 5),
  },
  footerText: {
    flexGrow: 0,
    lineHeight: 1,
    alignContent: 'center',
    paddingRight: theme.spacing(1.5),
  },
  footerButton: {
    flexGrow: 0,
    alignSelf: 'center',
    textTransform: 'none',
    height: theme.spacing(5),
  },
}));

const UserConsentModal = (props: UserConsentModalProps) => {
  const { open, onClick, loading } = props;
  const { classes } = useStyles();

  return (
    <ModalV2.Modal
      id={ETHICAL_PROMPT_MODAL_ID}
      hideBackdrop
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      header={
        <Box className={classes.header}>
          <Typography
            component="h1"
            variant="h5"
            bold
            color="tertiary"
            className={classes.title}
          >
            {`WellSaid Acceptable Use Policy`}
          </Typography>
        </Box>
      }
      footer={
        <Box className={classes.footer}>
          <Box className={classes.footerText}>
            <Typography variant="pMd" inline>
              {`By selecting `}
            </Typography>
            <Typography variant="pMd" bold inline>
              {`Agree and continue`}
            </Typography>
            <Typography variant="pMd" inline>
              {`, you agree to the `}
            </Typography>
            <TypographyLink
              variant="pMd"
              underlined
              inline
              href="/legal?document=wellsaid_acceptable_use_policy"
              target="_blank"
            >
              {`WellSaid Acceptable Use Policy`}
            </TypographyLink>
          </Box>
          <Button
            variant="primary"
            className={classes.footerButton}
            type={'button'}
            onClick={() => {
              if (onClick) onClick();
            }}
            size={'large'}
            loading={loading}
            disabled={loading}
            fullWidth={false}
          >
            <Typography variant="h4" color="gray_500" bold>
              {`Agree and continue`}
            </Typography>
          </Button>
        </Box>
      }
      onClick={onClick}
      loading={loading}
    >
      <Box className={classes.modalBody}>
        <Box className={classes.bodyHeader}>
          <Box className={classes.heartIcon}>
            <img
              src="/static/images/logo/v2/heart2.svg"
              alt="WellSaid Heart"
              height="30px"
            />
          </Box>
          <Typography
            variant="h2"
            bold
          >{`We're committed to ethical AI. Here's what you should know.`}</Typography>
        </Box>
        <Box className={classes.bodyParagraph}>
          <Typography variant="h3" bold>{`Your data is yours`}</Typography>
          <Typography
            variant="pMd"
            color="gray_300"
          >{`Our voice models are built on private, consented data—not your scripts.`}</Typography>
        </Box>
        <Box className={classes.bodyParagraph}>
          <Typography variant="h3" bold>{`No deepfakes. Ever.`}</Typography>
          <Typography
            variant="pMd"
            color="gray_300"
          >{`All our voices are responsibly sourced from paid, consenting actors—not scraped from the internet.`}</Typography>
        </Box>
        <Box className={classes.bodyParagraph}>
          <Typography
            variant="h3"
            bold
          >{`Security and privacy comes built in`}</Typography>
          <Typography
            variant="pMd"
            color="gray_300"
          >{`We’re SOC2 and GDPR compliant. Your data is encrypted and never used to train our models or sold.`}</Typography>
        </Box>
        <Box className={classes.bodyParagraph}>
          <Typography
            variant="h3"
            bold
          >{`We prohibit misuse of our platform`}</Typography>
          <Box className={classes.typographyBlock}>
            <Typography
              variant="pMd"
              color="gray_300"
              inline
            >{`The following content is expressly prohibited per the `}</Typography>
            <Typography
              variant="pMd"
              color="gray_300"
              bold
              inline
            >{`WellSaid Acceptable Use Policy`}</Typography>
            <Typography
              variant="pMd"
              color="gray_300"
              inline
            >{`. This list is not exhaustive, more detail can be found `}</Typography>
            <TypographyLink
              variant="pMd"
              color="gray_300"
              underlined
              inline
              href="/legal?document=wellsaid_acceptable_use_policy"
              target="_blank"
            >{`here`}</TypographyLink>
            <Typography variant="pMd" color="gray_300" inline>{`.`}</Typography>
          </Box>

          <Typography variant="pMd">
            <ul>
              <li>
                <span>{`Hateful & violent content`}</span>
              </li>
              <li>
                <span>{`Voting & election disruption content`}</span>
              </li>
              <li>
                <span>{`Scams, spam, and illegal content`}</span>
              </li>
              <li>
                <span>{`Sexually explicit content`}</span>
              </li>
            </ul>
          </Typography>
        </Box>
      </Box>
    </ModalV2.Modal>
  );
};

export default UserConsentModal;
